'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Home, Grid2x2, ShoppingBag, Smartphone } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCart } from '@/hooks/useCart';

const DEVICE_DETAIL_PREFIX = '/products/device/';

function isFinderPath(pathname: string): boolean {
  return pathname === '/devices' || pathname.startsWith('/devices/') || pathname.startsWith(DEVICE_DETAIL_PREFIX);
}

function isProductsPath(pathname: string): boolean {
  return pathname === '/products' || (pathname.startsWith('/products/') && !pathname.startsWith(DEVICE_DETAIL_PREFIX));
}

const TABS = [
  { href: '/', label: 'الرئيسية', icon: Home },
  { href: '/products', label: 'المنتجات', icon: Grid2x2 },
  { href: '/devices', label: 'الموديلات', icon: Smartphone },
  { href: '/order', label: 'السلة', icon: ShoppingBag },
] as const;

export function BottomNav() {
  const pathname = usePathname();
  const { itemCount } = useCart();
  // Cart persists to localStorage: the server always renders 0, so defer
  // the badge to post-hydration — otherwise React throws a hydration
  // mismatch for every visitor with a non-empty cart.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <nav
      dir="rtl"
      className="fixed inset-x-0 bottom-0 z-40 flex items-center justify-around border-t border-border bg-background/95 px-2 pt-2 pb-[max(env(safe-area-inset-bottom),8px)] backdrop-blur md:hidden"
    >
      {TABS.map(({ href, label, icon: Icon }) => {
        const isActive =
          href === '/'
            ? pathname === '/'
            : href === '/devices'
              ? isFinderPath(pathname)
              : href === '/products'
                ? isProductsPath(pathname)
                : pathname.startsWith(href);

        return (
          <Link key={href} href={href} className="relative flex min-h-12 flex-1 flex-col items-center justify-center gap-1 py-1">
            <Icon className={cn('h-6 w-6', isActive ? 'text-primary' : 'text-muted-foreground')} />
            <span className={cn('text-[11px] font-bold', isActive ? 'text-primary' : 'text-muted-foreground')}>
              {label}
            </span>
            {href === '/order' && mounted && itemCount > 0 && (
              <span className="absolute -top-0.5 right-6 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
                {itemCount}
              </span>
            )}
          </Link>
        );
      })}
    </nav>
  );
}
